import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { ADDON_META, ADDON_STATUS } from "@/lib/addons";
import { fmtDay } from "@/lib/daytime";

function Detail({ a, onClose }) {
  const rows = [["Pickup", a.pickup_location], ["Destination", a.destination], ["Arrival", a.arrival_time], ["Return trip", a.service === "ride" ? (a.return_required ? "Yes" : "No") : ""],
    ["Receiving adult", a.receiving_adult], ["Appointment", [a.appointment_type, a.appointment_location].filter(Boolean).join(" · ")], ["Return to", a.service === "escort" ? a.return_destination : ""],
    ["Emergency contact", a.emergency_contact], ["Authorized pickup", a.authorized_pickup], ["Medical", a.medical_info], ["Notes", a.notes], ["Trip status", a.trip_status?.replace("_", " ")]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="extra-detail-dialog">
        <DialogHeader><DialogTitle className="font-display text-navy">{ADDON_META[a.service].name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{fmtDay(a.date, { weekday: "long", month: "long", day: "numeric" })} · {a.start}{a.end ? `–${a.end}` : ""} · {a.child_names.join(", ")}</p>
        <div className="text-sm space-y-1">{rows.filter(([, v]) => v).map(([k, v]) => <p key={k}><b>{k}:</b> {v}</p>)}</div>
        <div className="bg-slate-50 rounded-xl p-2 text-sm">{a.quote.lines.map((l, i) => <p key={i} className="flex justify-between"><span>{l.label}</span><span>${l.amount}</span></p>)}
          {a.quote.discount > 0 && <p className="flex justify-between text-emerald-600"><span>Member discount</span><span>−${a.quote.discount}</span></p>}
          <p className="flex justify-between font-bold"><span>Total</span><span>${a.price}</span></p></div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ a, onChange, onPay }) {
  const [view, setView] = useState(false);
  const [moving, setMoving] = useState(false);
  const [nd, setNd] = useState({ date: a.date, start: a.start, end: a.end });
  const cancel = async () => {
    if (!window.confirm(`Cancel this ${ADDON_META[a.service].name}?`)) return;
    try { const { data } = await api.post(`/addons/${a.id}/cancel`); toast.success(data.message); onChange(); } catch (e) { toast.error(apiErr(e)); }
  };
  const move = async () => { try { await api.post(`/addons/${a.id}/reschedule`, nd); toast.success("Rescheduled — our team will re-confirm"); setMoving(false); onChange(); } catch (e) { toast.error(apiErr(e)); } };
  const I = ADDON_META[a.service].icon;
  const canEdit = ["requested", "confirmed"].includes(a.status);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid={`extra-${a.id}`}>
      <div className="flex items-start gap-3">
        <I className="w-6 h-6 mt-1 shrink-0" style={{ color: ADDON_META[a.service].color }} />
        <div className="flex-1">
          <p className="font-bold text-navy">{ADDON_META[a.service].name} <span className={`ml-1 text-[10px] uppercase rounded-full px-2 py-0.5 ${ADDON_STATUS[a.status]}`} data-testid={`extra-status-${a.id}`}>{a.status.replace("_", " ")}</span></p>
          <p className="text-xs text-slate-500">{fmtDay(a.date)} · {a.start}{a.end ? `–${a.end}` : ""} · {a.child_names.join(", ")} · ${a.price}{a.trip_status ? ` · ${a.trip_status.replace("_", " ")}` : ""}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setView(true)} data-testid={`extra-view-${a.id}`}>VIEW DETAILS</Button>
        {canEdit && !a.paid && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setMoving(!moving)} data-testid={`extra-reschedule-${a.id}`}>RESCHEDULE</Button>}
        {canEdit && <Button size="sm" variant="outline" className="rounded-full text-coral" onClick={cancel} data-testid={`extra-cancel-${a.id}`}>CANCEL</Button>}
        {a.order && a.order.status === "pending" && <Button size="sm" className="rounded-full bg-gold text-navy font-bold" onClick={() => onPay(a.order)} data-testid={`extra-pay-${a.id}`}>Pay ${a.order.amount}</Button>}
      </div>
      {moving && <div className="grid grid-cols-3 gap-2 mt-2">
        <Input type="date" value={nd.date} onChange={(e) => setNd({ ...nd, date: e.target.value })} className="rounded-xl col-span-3 sm:col-span-1" data-testid={`extra-new-date-${a.id}`} />
        <Input type="time" value={nd.start} onChange={(e) => setNd({ ...nd, start: e.target.value })} className="rounded-xl" />
        {(a.service === "wr" || a.service === "rr") && <Input type="time" value={nd.end} onChange={(e) => setNd({ ...nd, end: e.target.value })} className="rounded-xl" />}
        <Button size="sm" className="rounded-full bg-navy col-span-3" onClick={move} data-testid={`extra-move-${a.id}`}>Save new time</Button>
      </div>}
      {view && <Detail a={a} onClose={() => setView(false)} />}
    </div>
  );
}

export function MyExtraServices({ data, onChange, onPay }) {
  return (
    <div data-testid="my-extra-services">
      <p className="font-display font-extrabold text-navy mb-2">MY EXTRA SERVICES</p>
      {data.upcoming.length === 0 ? <p className="text-sm text-slate-400">No extra services booked.</p> :
        <div className="grid sm:grid-cols-2 gap-2">{data.upcoming.map((a) => <Row key={a.id} a={a} onChange={onChange} onPay={onPay} />)}</div>}
      {data.past.length > 0 && <p className="text-xs text-slate-400 mt-2">Past: {data.past.slice(0, 6).map((a) => `${ADDON_META[a.service].name} ${fmtDay(a.date)} (${a.status.replace("_", " ")})`).join(" · ")}</p>}
    </div>
  );
}
